import { useEffect, useRef, useState } from "react";
import {
    Box,
    Container,
    Typography,
    Paper,
    Grid,
    Chip,
    Button,
    IconButton,
    Divider,
    Stack,
    Tooltip,
    Alert,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Tabs,
    Tab,
    TextField,
    Fade,
    CircularProgress,
    Select,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    ChevronLeft as ChevronLeftIcon,
    PlayArrow as PlayArrowIcon,
    Stop as StopIcon,
    Refresh as RefreshIcon,
    Delete as DeleteIcon,
    Settings as SettingsIcon,
    MoreVert as MoreVertIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Pending as PendingIcon,
    Circle as CircleIcon,
    Public as PublicIcon,
    CalendarToday as CalendarIcon,
    CloudQueue as CloudIcon,
    Info as InfoIcon,
    Chat as ChatIcon,
    Code as CodeIcon,
    Send as SendIcon,
    ContentCopy as CopyIcon,
    Check as CheckIcon,
    AttachFile as AttachFileIcon,
    InsertDriveFile as InsertDriveFileIcon,
    Dns as DnsIcon,
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    Close as CloseIcon
} from '@mui/icons-material';


import { chatService } from '../../services/ChatService'
import { deploymentInstanceService } from '../../services/DeploymentInstanceService';
import { promptLabService } from '../../services/PromptLabService';
import DOMPurify from "dompurify";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import { useParams } from "react-router-dom";

interface Message {
    id: string;
    role: 'user' | 'assistant';
    content: any;
}

export function PlayGround({ deployment }: { deployment: any }) {

    const { deploymentId } = useParams<{ deploymentId: string }>();

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoadingResponse, setIsLoadingResponse] = useState(false);
    const [previousResponse, setPreviousResponse] = useState<{ role: string; content: any }[]>([]);
    const [truncationWarning, setTruncationWarning] = useState<string | null>(null);
    const [inputError, setInputError] = useState<string | null>(null);
    const [showResetDialog, setShowResetDialog] = useState(false);

    // Usage category and instance selection
    const [usageCategory, setUsageCategory] = useState('');
    const [usageTypes, setUsageTypes] = useState<any[]>([]);
    const [loadingUsageTypes, setLoadingUsageTypes] = useState(true);
    const [selectedPrompt, setSelectedPrompt] = useState('');
    const [prompts, setPrompts] = useState<any[]>([]);
    const [loadingPrompts, setLoadingPrompts] = useState(true);
    const [selectedVersion, setSelectedVersion] = useState('');
    const [versions, setVersions] = useState<any[]>([]);
    const [currentVersionTag, setCurrentVersionTag] = useState<number | null>(null);
    const [selectedInstance, setSelectedInstance] = useState('');
    const [instances, setInstances] = useState<any[]>([]);
    const [loadingInstances, setLoadingInstances] = useState(true);
    
    // Direct system prompt state
    const [showSystemPromptDialog, setShowSystemPromptDialog] = useState(false);
    const [systemPrompt, setSystemPrompt] = useState('');
    const [tempSystemPrompt, setTempSystemPrompt] = useState('');
    
    // Clear system prompt when user starts selecting from dropdowns
    useEffect(() => {
        // Only clear system prompt if user is actively selecting dropdown values (not clearing them)
        if ((usageCategory || selectedPrompt || selectedVersion) && systemPrompt.trim()) {
            setSystemPrompt('');
        }
    }, [usageCategory, selectedPrompt, selectedVersion]);
    
    // Clear dropdowns when user enters system prompt
    useEffect(() => {
        // Only clear dropdowns if user is actively entering system prompt (not clearing it)
        if (systemPrompt.trim() && (usageCategory || selectedPrompt || selectedVersion)) {
            setUsageCategory('');
            setSelectedPrompt('');
            setSelectedVersion('');
        }
    }, [systemPrompt]);

    useEffect(() => {
        console.log("Deployment ID changed in Playground:", deployment);
    }, [deployment]);
    
    // Load usage types
    useEffect(() => {
        const fetchUsageTypes = async () => {
            try {
                const data = await promptLabService.getUsageTypes();
                setUsageTypes(data);
            } catch (error) {
                console.error('Failed to load usage types:', error);
            } finally {
                setLoadingUsageTypes(false);
            }
        };

        fetchUsageTypes();
    }, []);

    // Load prompts
    useEffect(() => {
        const fetchPrompts = async () => {
            try {
                const data = await promptLabService.getPrompts();
                setPrompts(data);
            } catch (error) {
                console.error('Failed to load prompts:', error);
            } finally {
                setLoadingPrompts(false);
            }
        };

        fetchPrompts();
    }, []);

    // Filter prompts based on selected usage category and update versions
    useEffect(() => {
        if (usageCategory && prompts.length > 0) {
            const selectedType = usageTypes.find(type => type.name === usageCategory);
            if (selectedType) {
                const filteredPrompts = prompts.filter(p => p.usage_type === selectedType.id);
                // Reset selections when category changes
                setSelectedPrompt('');
                setSelectedVersion('');
                setVersions([]);
            }
        } else {
            setSelectedPrompt('');
            setSelectedVersion('');
            setVersions([]);
        }
    }, [usageCategory, prompts, usageTypes]);

    // Update versions when prompt is selected
    useEffect(() => {
        if (selectedPrompt && prompts.length > 0) {
            const prompt = prompts.find(p => p.name === selectedPrompt);
            if (prompt && prompt.versions && Array.isArray(prompt.versions)) {
                const sortedVersions = [...prompt.versions].sort((a, b) => b.version_tag - a.version_tag);
                setVersions(sortedVersions);
                // Store the current version tag
                setCurrentVersionTag(prompt.current_version || null);
                // Auto-select the latest version (current_version)
                if (prompt.current_version) {
                    setSelectedVersion(String(prompt.current_version));
                }
            } else {
                setVersions([]);
                setSelectedVersion('');
                setCurrentVersionTag(null);
            }
        } else {
            setVersions([]);
            setSelectedVersion('');
            setCurrentVersionTag(null);
        }
    }, [selectedPrompt, prompts]);

    // Load deployment instances
    useEffect(() => {
        const fetchInstances = async () => {
            try {
                const data = await deploymentInstanceService.getDeploymentInstances();
                // Filter to only show instances with status "Running"
                const runningInstances = (data || []).filter(
                    (instance: any) => instance.instance_status === 'Running'
                );
                setInstances(runningInstances);
            } catch (error) {
                console.error('Failed to load deployment instances:', error);
            } finally {
                setLoadingInstances(false);
            }
        };

        fetchInstances();
    }, []);
    
    // Playground state
    const [messages, setMessages] = useState<Message[]>([
        {
            id: '1',
            role: 'assistant',
            content: 'Hello! I\'m ready to help you test this deployed model. Send me a message to get started.',
        }
    ]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleRemoveFile = (index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        setSelectedFiles(prev => [...prev, ...files]);
    };

    const LLMResponse = (rawContent: any) => {
        const cleaned = DOMPurify.sanitize(rawContent, { ALLOWED_TAGS: [] }).trim();

        // 2️⃣ Split by blocks (paragraphs / code fences)
        const blocks = cleaned.split(/(?:\n{2,}|(```[\s\S]*?```))/g).filter(Boolean);

        return (
            <div className="flex flex-col gap-4">
                {blocks.map((block, i) => (
                    <div
                        key={i}
                        className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-800 shadow-sm"
                        suppressHydrationWarning
                    >
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeHighlight]}
                            skipHtml // ⛔ disables raw HTML rendering — no nested <p> issues
                        >
                            {block.trim()}
                        </ReactMarkdown>
                    </div>
                ))}
            </div>
        );
    }

    const handleSendMessage = async () => {
        if (!inputMessage.trim() && selectedFiles.length === 0) return;
        
        // Check if either system prompt is provided OR all three selections are made
        const hasSystemPrompt = systemPrompt.trim().length > 0;
        const hasFullSelection = usageCategory && selectedPrompt && selectedVersion;
        
        if (!hasSystemPrompt && !hasFullSelection) {
            if (!usageCategory && !selectedPrompt && !selectedVersion) {
                setInputError('Please either enter a system prompt or select usage category, prompt template, and version.');
            } else if (!usageCategory) {
                setInputError('Please select a usage category before sending a message.');
            } else if (!selectedPrompt) {
                setInputError('Please select a prompt before sending a message.');
            } else if (!selectedVersion) {
                setInputError('Please select a version before sending a message.');
            }
            return;
        }

        
        // Don't allow sending if reset dialog is shown
        if (showResetDialog) return;
        
        setTruncationWarning(null);
        setInputError(null);

        const { model_name, provider } = deployment?.instance_config || {};
        let prompt;
        if (previousResponse.length) {
            prompt = [
                ...previousResponse,
                {
                    "role": "user",
                    "content": inputMessage
                },
            ];
        } else {
            prompt = [{
                "role": "user",
                "content": inputMessage
            }];
        }
        console.log("Sending prompt:", JSON.stringify(prompt, null, 2));

        // Get the prompt name from selected prompt
        const promptObj = prompts.find(p => p.name === selectedPrompt);
        const promptName = promptObj?.name || '';

        const chatDetail: any = {
            prompt,
            url: deployment?.end_point,
            provider,
            models: model_name,
            instance_id: deployment?.instance_id,
        };
        
        // Add either system_prompt or the selection-based fields
        if (hasSystemPrompt) {
            chatDetail.system_prompt = systemPrompt;
        } else {
            chatDetail.usage_category = usageCategory;
            chatDetail.prompt_name = promptName;
            chatDetail.version = selectedVersion;
        }

        const userMessage: Message = {
            id: Date.now().toString(),
            role: 'user',
            content: inputMessage,
        };
        const assistantId = (Date.now() + 1).toString();
        const currentInputMessage = inputMessage;
        setInputMessage('');
        setIsLoadingResponse(true);

        let fullResponse = "";
        let assistantMessageAdded = false;
        let messageSuccessful = false;

        chatService.streamChatCompletion(
            chatDetail,

            // 🔹 onToken
            (token) => {
                fullResponse += token;
                
                if (!assistantMessageAdded) {
                    // Add both user and assistant message on first token (success)
                    assistantMessageAdded = true;
                    messageSuccessful = true;
                    setMessages(prev => [...prev, userMessage, {
                        id: assistantId,
                        role: 'assistant',
                        content: LLMResponse(fullResponse)
                    }]);
                    // Also update previousResponse with user message
                    setPreviousResponse(prev => [...prev, {
                        "role": "user",
                        "content": currentInputMessage
                    }]);
                } else {
                    // Update existing assistant message
                    setMessages(prev =>
                        prev.map(m =>
                            m.id === assistantId
                                ? { ...m, content: LLMResponse(fullResponse) }
                                : m
                        )
                    );
                }
                // Hide loading indicator as soon as first token is received
                setIsLoadingResponse(false);
            },

            // 🔹 onDone
            () => {
                setIsLoadingResponse(false);
                if (messageSuccessful) {
                    setPreviousResponse(prev => [
                        ...prev,
                        { role: "assistant", content: fullResponse }
                    ]);
                }
            },

            // 🔹 onError
            (err) => {
                setIsLoadingResponse(false);
                if (err?.type === "TOKEN_LIMIT_EXCEEDED") {
                    setInputError(err.message || "Token limit exceeded");
                    setShowResetDialog(true);
                    // Don't add message to history
                    return;
                }
                console.error(err);
                
            },

            // onWarning
            (warning) => {
                if (
                    warning?.type === 'RESPONSE_TRUNCATED'
                ) {
                    setTruncationWarning(
                        warning.message ?? 'Response was truncated due to token limit.'
                    );
                    setShowResetDialog(true);
                }
            }
        );
    };

    const handleResetConversation = () => {
        setPreviousResponse([]);
        setMessages([{
            id: '1',
            role: 'assistant',
            content: 'Hello! I\'m ready to help you test this deployed model. Send me a message to get started.',
        }]);
        setInputError(null);
        setTruncationWarning(null);
        setShowResetDialog(false);
    };

    return (
        <>
            <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 500px)', minHeight: 500 }}>
                {/* Messages */}
                <Box sx={{ flex: 1, overflow: 'auto', mb: 3, pr: 2 }}>
                {messages.map((message) => (
                    <Fade in key={message.id}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                                mb: 2
                            }}
                        >
                            <Paper
                                elevation={0}
                                sx={{
                                    maxWidth: '70%',
                                    p: 3,
                                    borderRadius: 3,
                                    bgcolor: message.role === 'user' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                                    background: message.role === 'user' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                                    color: message.role === 'user' ? 'white' : '#0f172a',
                                    border: message.role === 'assistant' ? '1px solid #e2e8f0' : 'none',
                                    boxShadow: message.role === 'user' ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none'
                                }}
                            >
                                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                                    {message.content}
                                </Typography>

                            </Paper>
                        </Box>
                    </Fade>
                ))}
                {truncationWarning && (
                    <Fade in>
                        <Alert 
                            severity="warning" 
                            icon={<ErrorIcon />}
                            onClose={() => {
                                setTruncationWarning(null);
                                setShowResetDialog(false);
                            }}
                            action={
                                showResetDialog && (
                                    <Stack direction="row" spacing={1}>
                                        <Button 
                                            color="inherit" 
                                            size="small"
                                            onClick={handleResetConversation}
                                            sx={{ fontWeight: 700 }}
                                        >
                                            Clear History
                                        </Button>
                                        <Button 
                                            color="inherit" 
                                            size="small"
                                            onClick={() => {
                                                setShowResetDialog(false);
                                                setTruncationWarning(null);
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </Stack>
                                )
                            }
                            sx={{ 
                                mb: 2,
                                borderRadius: 2,
                                '& .MuiAlert-message': {
                                    fontWeight: 500
                                }
                            }}
                        >
                            {truncationWarning}
                            {showResetDialog && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    Would you like to clear the conversation history to reduce token usage?
                                </Typography>
                            )}
                        </Alert>
                    </Fade>
                )}

                <div ref={messagesEndRef} />
            </Box>

            {/* Loading Indicator */}
            {isLoadingResponse && (
                <Fade in>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            mb: 2
                        }}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                bgcolor: 'white',
                                border: '1px solid #e2e8f0'
                            }}
                        >
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <CircularProgress size={20} sx={{ color: '#667eea' }} />
                                <Typography variant="body1" sx={{ color: '#64748b' }}>
                                    Thinking...
                                </Typography>
                            </Stack>
                        </Paper>
                    </Box>
                </Fade>
            )}

            {/* Inline input error */}
            {inputError && (
                <Fade in>
                    <Alert
                        severity="error"
                        sx={{ mb: 2, borderRadius: 2 }}
                        onClose={() => setInputError(null)}
                        action={
                            showResetDialog && (
                                <Stack direction="row" spacing={1}>
                                    <Button 
                                        color="inherit" 
                                        size="small"
                                        onClick={handleResetConversation}
                                        sx={{ fontWeight: 700 }}
                                    >
                                        Clear History
                                    </Button>
                                    <Button 
                                        color="inherit" 
                                        size="small"
                                        onClick={() => setShowResetDialog(false)}
                                    >
                                        Cancel
                                    </Button>
                                </Stack>
                            )
                        }
                    >
                        {inputError}
                        {showResetDialog && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Would you like to clear the conversation history to reduce token usage?
                            </Typography>
                        )}
                    </Alert>
                </Fade>
            )}


            {/* Selected Files Preview */}
            {selectedFiles.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: '#fafbfc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, mb: 1, display: 'block', color: '#64748b' }}>
                            Attached Files ({selectedFiles.length})
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            {selectedFiles.map((file, index) => (
                                <Chip
                                    key={index}
                                    label={file.name}
                                    onDelete={() => handleRemoveFile(index)}
                                    icon={<InsertDriveFileIcon />}
                                    sx={{ mb: 1 }}
                                />
                            ))}
                        </Stack>
                    </Paper>
                </Box>
            )}

            {/* Input Area */}
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    bgcolor: '#fafbfc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 3
                }}
            >
                {/* Configuration Row */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, pb: 2, borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>

                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Usage Category</InputLabel>
                        <Select
                            value={usageCategory}
                            label="Usage Category"
                            onChange={(e) => setUsageCategory(e.target.value)}
                            disabled={loadingUsageTypes || usageTypes.length === 0}
                            sx={{ bgcolor: 'white' }}
                        >
                            {loadingUsageTypes ? (
                                <MenuItem value="" disabled>
                                    <CircularProgress size={20} sx={{ mr: 1 }} /> Loading...
                                </MenuItem>
                            ) : usageTypes.length === 0 ? (
                                <MenuItem value="" disabled>
                                    No usage types
                                </MenuItem>
                            ) : (
                                usageTypes.map((type) => (
                                    <MenuItem key={type.id} value={type.name}>
                                        {type.name}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Prompt Template</InputLabel>
                        <Select
                            value={selectedPrompt}
                            label="Prompt Template"
                            onChange={(e) => setSelectedPrompt(e.target.value)}
                            disabled={!usageCategory || loadingPrompts}
                            sx={{ bgcolor: 'white' }}
                        >
                            {!usageCategory ? (
                                <MenuItem value="" disabled>
                                    Select category first
                                </MenuItem>
                            ) : loadingPrompts ? (
                                <MenuItem value="" disabled>
                                    <CircularProgress size={20} sx={{ mr: 1 }} /> Loading...
                                </MenuItem>
                            ) : (() => {
                                const selectedType = usageTypes.find(type => type.name === usageCategory);
                                const filteredPrompts = selectedType 
                                    ? prompts.filter(p => p.usage_type === selectedType.id)
                                    : [];
                                
                                return filteredPrompts.length === 0 ? (
                                    <MenuItem value="" disabled>
                                        No prompts available
                                    </MenuItem>
                                ) : (
                                    filteredPrompts.map((prompt) => (
                                        <MenuItem key={prompt.id} value={prompt.name}>
                                            {prompt.name}
                                        </MenuItem>
                                    ))
                                );
                            })()}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Version</InputLabel>
                        <Select
                            value={selectedVersion}
                            label="Version"
                            onChange={(e) => setSelectedVersion(e.target.value)}
                            disabled={!selectedPrompt || versions.length === 0}
                            sx={{ bgcolor: 'white' }}
                        >
                            {!selectedPrompt ? (
                                <MenuItem value="" disabled>
                                    Select prompt first
                                </MenuItem>
                            ) : versions.length === 0 ? (
                                <MenuItem value="" disabled>
                                    No versions available
                                </MenuItem>
                            ) : (
                                versions.map((version) => (
                                    <MenuItem key={version.id} value={String(version.version_tag)}>
                                        Version {version.version_tag} {version.version_tag === currentVersionTag ? '(Latest)' : ''}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </FormControl>

                    {/* Direct System Prompt Button */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        ml: 'auto',
                        gap: 1
                    }}>
                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => {
                                setTempSystemPrompt(systemPrompt);
                                setShowSystemPromptDialog(true);
                            }}
                            sx={{
                                borderColor: systemPrompt ? '#10b981' : '#cbd5e1',
                                color: systemPrompt ? '#10b981' : '#64748b',
                                bgcolor: systemPrompt ? '#ecfdf5' : 'white',
                                fontWeight: 600,
                                '&:hover': {
                                    borderColor: '#667eea',
                                    bgcolor: '#f1f5f9'
                                }
                            }}
                        >
                            {systemPrompt ? 'Edit System Prompt' : 'Enter System Prompt'}
                        </Button>
                        {systemPrompt && (
                            <Chip
                                label="Direct"
                                size="small"
                                sx={{
                                    bgcolor: '#10b981',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.7rem'
                                }}
                            />
                        )}
                    </Box>

                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        multiple
                        style={{ display: 'none' }}
                    />
                    {/* <Tooltip title="Attach file">
                        <IconButton
                            onClick={() => fileInputRef.current?.click()}
                            sx={{
                                bgcolor: 'white',
                                border: '1px solid #e2e8f0',
                                '&:hover': {
                                    bgcolor: '#f8fafc',
                                    borderColor: '#667eea'
                                }
                            }}
                        >
                            <AttachFileIcon />
                        </IconButton>
                    </Tooltip> */}
                    <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendMessage();
                            }
                        }}
                        placeholder="Type your message..."
                        variant="outlined"
                        disabled={showResetDialog}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                bgcolor: 'white',
                                '& fieldset': {
                                    borderColor: '#e2e8f0'
                                },
                                '&:hover fieldset': {
                                    borderColor: '#cbd5e1'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#667eea'
                                }
                            }
                        }}
                    />
                    <Button
                        variant="contained"
                        endIcon={<SendIcon />}
                        onClick={handleSendMessage}
                        disabled={
                            !inputMessage.trim() || 
                            (!systemPrompt.trim() && (!usageCategory || !selectedPrompt || !selectedVersion))
                        }
                        sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            minWidth: 120,
                            height: 56,
                            fontWeight: 700,
                            '&:hover': {
                                background: 'linear-gradient(135deg, #5568d3 0%, #6b3f94 100%)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 8px 16px rgba(102, 126, 234, 0.4)'
                            },
                            '&:disabled': {
                                background: '#e2e8f0',
                                color: '#94a3b8'
                            },
                            transition: 'all 0.3s'
                        }}
                    >
                        Send
                    </Button>
                </Box>
                
            </Paper>
        </Box>
        
        {/* System Prompt Dialog */}
        <Dialog 
            open={showSystemPromptDialog} 
            onClose={() => setShowSystemPromptDialog(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                            Direct System Prompt
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mt: 1 }}>
                            Enter your system prompt directly instead of selecting from templates
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={() => setShowSystemPromptDialog(false)}
                        sx={{
                            color: '#64748b',
                            '&:hover': {
                                bgcolor: '#f1f5f9'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                {/* Code Editor Style Textarea */}
                <Box
                    sx={{
                        border: '1px solid #e2e8f0',
                        borderRadius: 1,
                        overflow: 'hidden',
                        bgcolor: '#1e293b',
                        mt: 2
                    }}
                >
                            {/* Editor Header */}
                            <Box
                                sx={{
                                    bgcolor: '#0f172a',
                                    px: 2,
                                    py: 1,
                                    borderBottom: '1px solid #334155',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ef4444' }} />
                                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#10b981' }} />
                                </Box>
                                <Typography variant="caption" sx={{ color: '#94a3b8', ml: 2 }}>
                                    system-prompt.txt
                                </Typography>
                            </Box>
                            
                            {/* Editor Content */}
                            <TextField
                                fullWidth
                                multiline
                                rows={20}
                                placeholder="Enter your system prompt here...&#10;&#10;Example:&#10;You are a helpful AI assistant. Always be polite and professional.&#10;Provide clear and concise answers."
                                value={tempSystemPrompt}
                                onChange={(e) => {
                                    const rawValue = e.target.value;
                                    const formattedValue = rawValue.split('\\n').join('\n');
                                    setTempSystemPrompt(formattedValue);
                                }}
                                onPaste={(e) => {
                                    e.preventDefault();
                                    const pastedText = e.clipboardData.getData('text');
                                    
                                    const formattedText = pastedText.split('\\n').join('\n');
                                    
                                    const textarea = e.target as HTMLTextAreaElement;
                                    const start = textarea.selectionStart;
                                    const end = textarea.selectionEnd;
                                    const currentValue = tempSystemPrompt;
                                    const newValue = currentValue.substring(0, start) + formattedText + currentValue.substring(end);
                                    setTempSystemPrompt(newValue);
                                }}
                                variant="standard"
                                InputProps={{
                                    disableUnderline: true,
                                    sx: {
                                        fontFamily: '"Fira Code", "Courier New", monospace',
                                        fontSize: '0.875rem',
                                        lineHeight: 1.7,
                                        color: '#e2e8f0',
                                        padding: 2,
                                        '& textarea': {
                                            '&::placeholder': {
                                                color: '#64748b',
                                                opacity: 1
                                            }
                                        }
                                    }
                                }}
                            />
                        </Box>
            </DialogContent>
            
            <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                <Button
                    variant="outlined"
                    onClick={() => {
                        setTempSystemPrompt('');
                    }}
                    disabled={!tempSystemPrompt.trim()}
                    sx={{
                        borderRadius: 2,
                        fontWeight: 600,
                        borderColor: '#e2e8f0',
                        color: '#64748b',
                        '&:hover': {
                            borderColor: '#cbd5e1',
                            bgcolor: '#f8fafc'
                        }
                    }}
                >
                    Clear
                </Button>
                <Button
                    variant="outlined"
                    onClick={() => setShowSystemPromptDialog(false)}
                    sx={{
                        borderRadius: 2,
                        fontWeight: 600,
                        borderColor: '#e2e8f0',
                        color: '#64748b',
                        '&:hover': {
                            borderColor: '#cbd5e1',
                            bgcolor: '#f8fafc'
                        }
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={() => {
                        // Apply the temp value to actual system prompt
                        setSystemPrompt(tempSystemPrompt);
                        // Clear selections when using direct system prompt
                        if (tempSystemPrompt.trim()) {
                            setUsageCategory('');
                            setSelectedPrompt('');
                            setSelectedVersion('');
                        }
                        setShowSystemPromptDialog(false);
                    }}
                    disabled={!tempSystemPrompt.trim()}
                    sx={{
                        borderRadius: 2,
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #5568d3 0%, #6b3f94 100%)'
                        },
                        '&:disabled': {
                            background: '#e2e8f0',
                            color: '#94a3b8'
                        }
                    }}
                >
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
}
